import { useApolloClient } from "@apollo/client";
import { assertBrandId } from "utils/assertions";
import { useCurrentBrandId } from "utils/currentUserHooks";
import type { OptionalKeys } from "utils/typescriptUtilis";
import BrandDocumentFileUpload, {
  BrandDocumentFileUploadOptions,
  CreatedBrandDocument,
} from "./BrandDocumentFileUpload";
import useMultipleDocumentUpload, {
  UseMultipleDocumentUploadOptions,
} from "./useMultipleDocumentUpload";

type UploadOptions<R> = Omit<
  BrandDocumentFileUploadOptions<R>,
  "file" | "apolloClient" | "brandId"
>;

export type UseMultipleBrandDocumentUploadOptions<R = CreatedBrandDocument> =
  OptionalKeys<
    UseMultipleDocumentUploadOptions<R, BrandDocumentFileUpload<R>>,
    "getDocumentFileUploadForFile"
  > & {
    getUploadOptions?: (file: File) => UploadOptions<R>;
    uploadOptions?: UploadOptions<R>;
  };

export default function useMultipleBrandDocumentUpload<
  R = CreatedBrandDocument,
>({
  getUploadOptions,
  uploadOptions,
  ...rest
}: UseMultipleBrandDocumentUploadOptions<R>) {
  const apolloClient = useApolloClient();
  const brandId = useCurrentBrandId();

  return useMultipleDocumentUpload<R, BrandDocumentFileUpload<R>>({
    getDocumentFileUploadForFile: (file) => {
      assertBrandId(brandId);
      return new BrandDocumentFileUpload<R>({
        file,
        apolloClient,
        brandId,
        ...uploadOptions,
        ...getUploadOptions?.(file),
      });
    },
    ...rest,
  });
}
